import React, { useState, useEffect } from 'react';
import './App.css';

const FULL_TITLE = 'GANchain Devnet Faucet';
const FULL_DESCRIPTION = 'Get free devnet tokens for the GANchain Devnet network to test your smart contracts, apps, and blockchain integrations.';

function App() {
  const [walletAddress, setWalletAddress] = useState('');
  const [isValid, setIsValid] = useState<boolean | null>(null);
  const [displayedTitle, setDisplayedTitle] = useState('');
  const [displayedDescription, setDisplayedDescription] = useState('');
  const [showCursor, setShowCursor] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [apiMessage, setApiMessage] = useState<{ 
    type: 'success' | 'error'; 
    text: string; 
    transactionHash?: string;
  } | null>(null);

  useEffect(() => {
    // Typing animation for title
    let titleIndex = 0;
    let descInterval: NodeJS.Timeout | null = null;
    
    const titleInterval = setInterval(() => {
      if (titleIndex < FULL_TITLE.length) {
        setDisplayedTitle(FULL_TITLE.substring(0, titleIndex + 1));
        titleIndex++;
      } else {
        clearInterval(titleInterval);
        // Start description animation after title completes
        setTimeout(() => {
          let descIndex = 0;
          descInterval = setInterval(() => {
            if (descIndex < FULL_DESCRIPTION.length) {
              setDisplayedDescription(FULL_DESCRIPTION.substring(0, descIndex + 1));
              descIndex++;
            } else {
              if (descInterval) clearInterval(descInterval);
              setShowCursor(false);
            }
          }, 20);
        }, 300);
      }
    }, 80);

    return () => {
      clearInterval(titleInterval);
      if (descInterval) clearInterval(descInterval);
    };
  }, []);

  const validateEVMAddress = (address: string): boolean => {
    // EVM address format: 0x followed by 40 hexadecimal characters
    const evmAddressRegex = /^0x[a-fA-F0-9]{40}$/;
    return evmAddressRegex.test(address);
  };

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const address = e.target.value.trim();
    setWalletAddress(address);
    setApiMessage(null); // Clear any previous messages when address changes
    
    if (address === '') {
      setIsValid(null);
    } else {
      setIsValid(validateEVMAddress(address));
    }
  };

  const handleGetGPU = async () => {
    if (!isValid || isLoading) return;

    setIsLoading(true);
    setApiMessage(null);

    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000';

    try {
      const response = await fetch(`${apiUrl}/faucet?address=${encodeURIComponent(walletAddress)}`);
      
      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      let data;
      
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        // Response is not JSON (likely HTML error page)
        const text = await response.text();
        console.error('Non-JSON response received:', text.substring(0, 500));
        setApiMessage({
          type: 'error',
          text: `Backend returned an error (${response.status}). Please check that the backend is running correctly at ${apiUrl}.`
        });
        setIsLoading(false);
        return;
      }

      if (response.ok && data.success) {
        // Success response with transaction hash
        setApiMessage({
          type: 'success',
          text: data.message || 'Tokens sent successfully!',
          transactionHash: data.transactionHash
        });
      } else {
        // Error response (rate limit or other errors)
        setApiMessage({
          type: 'error',
          text: data.message || data.error || 'Failed to claim tokens. Please try again later.'
        });
      }
    } catch (error) {
      console.error('API Error:', error);
      setApiMessage({
        type: 'error',
        text: error instanceof Error && error.message.includes('fetch')
          ? `Cannot connect to backend. Please make sure the backend is running at ${apiUrl}`
          : 'Network error. Please make sure the backend is running and try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="App">
      <div className="page-grid">
        <div className="column"></div>
        <div className="column">
          <h1 className="main-title">
            {displayedTitle}
            {showCursor && (displayedTitle.length < FULL_TITLE.length || displayedDescription.length < FULL_DESCRIPTION.length) && (
              <span className="typing-cursor">|</span>
            )}
          </h1>
          <p className="main-description">
            {displayedDescription}
            {showCursor && displayedTitle.length === FULL_TITLE.length && displayedDescription.length < FULL_DESCRIPTION.length && (
              <span className="typing-cursor">|</span>
            )}
          </p>
        </div>
        <div className="column">
          <div className="faucet-card">
            <h1 className="faucet-title">Claim Devnet tokens</h1>
            <p className="faucet-subtitle">Maximum of 10 $GPU every 8 hours</p>
            
            <div className="input-container">
              <input
                type="text"
                className={`wallet-input ${isValid === false ? 'invalid' : ''} ${isValid === true ? 'valid' : ''}`}
                placeholder="0x..."
                value={walletAddress}
                onChange={handleAddressChange}
                autoComplete="off"
                spellCheck="false"
              />
              {isValid === false && walletAddress !== '' && (
                <p className="error-message">Invalid EVM wallet address</p>
              )}
              {isValid === true && !apiMessage && (
                <p className="success-message">Valid address</p>
              )}
              {apiMessage && (
                <div className={apiMessage.type === 'success' ? 'success-message' : 'error-message'}>
                  <p style={{ margin: 0 }}>{apiMessage.text}</p>
                  {apiMessage.type === 'success' && apiMessage.transactionHash && (
                    <p className="transaction-hash">
                      Transaction: <span className="tx-hash-value">{apiMessage.transactionHash}</span>
                    </p>
                  )}
                </div>
              )}
            </div>

            <button
              className="get-gpu-button"
              onClick={handleGetGPU}
              disabled={!isValid || isLoading}
            >
              {isLoading ? 'Processing...' : 'Get GPU'}
            </button>
          </div>
        </div>
        <div className="column"></div>
      </div>
    </div>
  );
}

export default App;
